import { useEffect, useRef } from 'react';

import { useFeatureFlags } from '../../feature-flags/presentation/FeatureFlagsContext';

// Points the feature-flag resolver at the workspace the user is VIEWING
// (the URL workspace), and loads that workspace's evaluated flags, whenever
// it changes — including direct deep-links and browser back/forward.
//
// Why this exists: me/summary resolves feature flags for the user's persisted
// ``active_workspace_id``, but the UI (sidebar gates, route paywalls) renders
// for the workspace in the URL. When those differ, the app gated the viewed
// workspace using the WRONG workspace's flags — e.g. a workspace-scoped
// ``feature.grants`` override didn't unlock Grants, bouncing the user to
// billing. Re-evaluating per viewed workspace makes that class of bug
// structurally impossible: a workspace's UI is only ever gated by its own
// flags.
//
// Deduped by the most-recent workspaceId so re-renders don't refire the
// fetch. ``selectWorkspace`` itself is a no-op fetch when workspaceId is
// falsy (auth/onboarding pages keep the flat summary flags).
export const useEnsureWorkspaceFeatureFlags = (
  workspaceId: string | null
): void => {
  const { selectWorkspace } = useFeatureFlags();
  const lastSelectedRef = useRef<string | null>(null);

  useEffect(() => {
    const normalized = workspaceId ? String(workspaceId) : null;
    if (!normalized) return;
    if (lastSelectedRef.current === normalized) return;
    lastSelectedRef.current = normalized;
    void selectWorkspace?.({ workspaceId: normalized });
  }, [workspaceId, selectWorkspace]);
};
